import { Reveal, SectionHead } from "@/components/site/motion";

const STORIES = [
  {
    stat: "30%",
    statLabel: "overall year-over-year program growth",
    org: "A Tax Resolution & Financial Services Company",
    tag: "Program Turnaround",
    situation: "The company’s top revenue producing partner relationship had declined sharply year over year, and the broader program had no clear way to prioritize where to invest.",
    did: "Ran a 30-day SWOT, built a segmentation model, and concentrated incentives and enablement on the highest value opportunities. Built partner scorecards and an executive dashboard.",
    result: "Reversed the largest partner relationship from decline to 33% positive growth, and lifted lead-to-revenue conversion to 43%, both contributing to the 30% overall program growth above.",
  },
  {
    stat: "$10M",
    statLabel: "pipeline generated in 60 days through one channel partner",
    org: "Clear Capital",
    tag: "Fintech SaaS for mortgage and proptech",
    situation: "The company had 30+ existing reseller, referral, and technology relationships worth $200M, but no structured program to activate them or recruit new partners.",
    did: "Built the partner program from the ground up, managed and activated the existing $200M portfolio, recruited and onboarded new reseller and referral partners, and owned the executive relationship with CGI, a global systems integrator, through a custom API integration.",
    result: "Increased partner revenue 200–300% among new resellers in year one, and opened a new monetized revenue channel through the CGI integration.",
  },
  {
    stat: "150%",
    statLabel: "increase in partner program profitability",
    org: "StarRez / Vista Equity Partners",
    tag: "Higher-ed SaaS",
    situation: "The platform had 200+ partner integrations, but only one was producing revenue, and its President and Board needed a clear partnership strategy.",
    did: "Renegotiated commercial agreements with tier-one partners to align incentives on both sides, architected the company’s first payments monetization strategy, and led a joint go-to-market and cloud migration initiative with Microsoft and Amazon for 1,300 customers.",
    result: "Increased partner program profitability 150% and monetized payments processing, opening a new revenue channel for the platform.",
  },
  {
    stat: "63%",
    statLabel: "partner-sourced revenue growth, following 50% the year before",
    org: "CoreLogic",
    tag: "Data and risk platform, proptech/fintech",
    situation: "A newly created channel partner role, built around prior performance, needed to establish a partner program from scratch inside a large, established organization.",
    did: "Built the Channel Partner Activation Program covering recruiting, onboarding, launch, and co-selling with sales and customer success, and identified RentTrack as an early first-mover partnership opportunity.",
    result: "Grew partner sourced revenue in consecutive years, and launched a new product capability and revenue line through the RentTrack partnership.",
  },
];

export function Stories() {
  return (
    <section id="results" className="section-border-top" data-testid="stories-section">
      <div className="wrap">
        <SectionHead
          chapter="Chapter 03"
          kicker="Track record"
          title="Four programs, four different starting points."
          lede="Each of these was a different kind of problem: a turnaround, a build from zero, a Board level strategy question, and a net-new function inside a large organization."
          testid="stories"
        />
        <div>
          {STORIES.map((s, i) => (
            <Reveal key={s.org} y={30} className="story" data-testid={`story-${i}`}>
              <div className="story-stat">
                <span className="story-figure" data-testid={`story-stat-${i}`}>{s.stat}</span>
                <span className="story-figure-label">{s.statLabel}</span>
              </div>
              <div>
                <div className="story-org">
                  {s.org}
                  <small>{s.tag}</small>
                </div>
                <div className="story-body">
                  <div className="story-col">
                    <h4>Situation</h4>
                    <p>{s.situation}</p>
                  </div>
                  <div className="story-col">
                    <h4>What I did</h4>
                    <p>{s.did}</p>
                  </div>
                  <div className="story-col">
                    <h4>Result</h4>
                    <p>{s.result}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const FIT_INDUSTRIES = [
  "Fintech & mortgage technology",
  "Proptech & real estate data",
  "Payments",
  "Enterprise SaaS & data infrastructure",
];

const FIT_SITUATIONS = [
  "B2B technology companies building a partner ecosystem for the first time",
  "PE and VC backed portfolio companies",
  "Companies entering a new vertical without an established partner playbook",
  "Companies formalizing a channel strategy as they scale",
];

const FIT_MODELS = [
  "Referral",
  "Reseller",
  "ISV",
  "OEM",
  "Embedded / API",
  "Systems Integrator / GSI",
  "Strategic Alliances",
  "Co-sell",
  "Marketplace",
];

export function BestFit() {
  return (
    <section id="best-fit" className="section-border-top" data-testid="best-fit-section">
      <div className="wrap">
        <SectionHead
          chapter="Chapter 04"
          kicker="Best fit"
          title="Where this works best."
          lede="This works best where partnerships are meant to be a growth engine, not a side project. One quick way to tell: the industries, the situations, and the partner models below."
          testid="best-fit"
        />
        <div className="fit-cols">
          <Reveal y={20} data-testid="fit-industries">
            <h4>Industries</h4>
            <ul>
              {FIT_INDUSTRIES.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
            <p className="fit-more">
              Additional industry experience: tax resolution &amp; financial services, higher education, hospitality,
              and logistics &amp; transportation.
            </p>
          </Reveal>
          <Reveal y={20} delay={0.08} data-testid="fit-situations">
            <h4>Company Situations</h4>
            <ul>
              {FIT_SITUATIONS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal y={20} delay={0.16} data-testid="fit-models">
            <h4>Partner Models</h4>
            <ul>
              {FIT_MODELS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
